import api from './api';

// Resolve API base (only used for the SignalR hub URL below; all HTTP calls go
// through the shared axios `api` instance which already handles auth + JWT refresh)
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

// ── Types ─────────────────────────────────────────────────────────────

export interface TrackingSessionResponse {
  id: number;
  token: string;
  planningId: number | null;
  targetSegmentId: number | null;
  name: string;
  status: string;
  /** Tracking source: "Phone" (browser GPS) or "Dako" (vehicle telematics) */
  source?: string;
  /** For DAKO sessions: id of the tracked transport method (null for phone sessions) */
  transportationId?: number | null;
  /** For DAKO sessions: display name of the tracked transport method */
  transportationName?: string | null;
  startedAt: string;
  stoppedAt: string | null;
  expiresAt: string;
  shareUrl?: string;
  startedByUserName?: string | null;
  deviceName?: string | null;
  startedByUserId?: string;
}

export interface TrackingPositionDto {
  latitude: number;
  longitude: number;
  speed: number | null;
  heading: number | null;
  accuracy: number | null;
  timestamp: string;
}

export interface TrackingLiveResponse {
  token: string;
  name: string;
  status: string;
  /** Tracking source: "Phone" (browser GPS) or "Dako" (vehicle telematics) */
  source?: string;
  startedAt: string;
  expiresAt: string;
  currentPosition: TrackingPositionDto | null;
  planningId: number | null;
  targetSegmentId: number | null;
}

export interface TrackingHistoryResponse {
  token: string;
  name: string;
  positions: TrackingPositionDto[];
}

export interface FlightPositionDto {
  flightNumber: string;
  callsign: string | null;
  latitude: number;
  longitude: number;
  altitudeMeters: number | null;
  speedKmh: number | null;
  heading: number | null;
  onGround: boolean;
  timestamp: string;
}

export interface FlightResponse {
  flight: FlightPositionDto | null;
  reason?: string;
  flightNumber?: string;
}

export interface StartTrackingRequest {
  planningId?: number | null;
  targetSegmentId?: number | null;
  name: string;
  expiryHours?: number;
  deviceName?: string;
}

export interface TrackingPositionRequest {
  latitude: number;
  longitude: number;
  speed?: number | null;
  heading?: number | null;
  accuracy?: number | null;
}

export interface StartDakoTrackingRequest {
  planningId?: number | null;
  targetSegmentId?: number | null;
  name: string;
  expiryHours?: number;
  deviceName?: string;
  /** Which DAKO-configured transport method to track (omit to use the global fallback). */
  transportationId?: number | null;
}

/** A transport method that has DAKO credentials configured (offered for source selection). */
export interface DakoCapableTransportation {
  transportationId: number;
  name: string;
  vehicleIdent: string;
  pollIntervalSeconds: number;
}

export interface DakoStatusResponse {
  /** True when DAKO tracking is available (a transport method is configured or the global fallback). */
  configured: boolean;
  companyIdent: string;
  vehicleIdent: string;
  pollIntervalSeconds: number;
  /** Transport methods that have DAKO credentials configured (for source selection). */
  transportations: DakoCapableTransportation[];
}

// ── API ───────────────────────────────────────────────────────────────
// All calls use the shared axios `api` instance. Its request interceptor adds
// the Bearer token and its response interceptor transparently refreshes an
// expired JWT (and retries the request), so tracking keeps working past the
// token lifetime instead of silently dying after ~75 minutes.

/** Start a new tracking session (auth required) */
export const startTracking = async (req: StartTrackingRequest): Promise<TrackingSessionResponse> => {
  const res = await api.post<TrackingSessionResponse>('/api/tracking/start', req);
  return res.data;
};

/** Send a position update (auth required) */
export const sendPosition = async (sessionId: number, req: TrackingPositionRequest): Promise<TrackingPositionDto> => {
  try {
    const res = await api.post<TrackingPositionDto>(`/api/tracking/${sessionId}/position`, req);
    return res.data;
  } catch (err: any) {
    // Surface the failure so callers can react (e.g. stop tracking after
    // repeated failures). Never swallow silently — that hid the 75-min timeout.
    console.warn('trackingApi.sendPosition failed:', err?.response?.status, err?.message);
    throw err;
  }
};

/** Get DAKO (vehicle telematics) tracking availability/status (auth required) */
export const getDakoStatus = async (): Promise<DakoStatusResponse> => {
  const res = await api.get<DakoStatusResponse>('/api/tracking/dako/status');
  return res.data;
};

/** Start a DAKO (vehicle telematics) tracking session (auth required). Positions are polled server-side. */
export const startDakoTracking = async (req: StartDakoTrackingRequest): Promise<TrackingSessionResponse> => {
  const res = await api.post<TrackingSessionResponse>('/api/tracking/dako/start', req);
  return res.data;
};

/** Stop a DAKO tracking session (auth required) */
export const stopDakoTracking = async (sessionId: number): Promise<TrackingSessionResponse> => {
  const res = await api.post<TrackingSessionResponse>(`/api/tracking/dako/${sessionId}/stop`);
  return res.data;
};

/** Update the target segment of an active session (auth required) */
export const updateTarget = async (sessionId: number, targetSegmentId: number | null): Promise<TrackingSessionResponse> => {
  const res = await api.post<TrackingSessionResponse>(`/api/tracking/${sessionId}/update-target`, { targetSegmentId });
  return res.data;
};

/** Stop a tracking session (auth required) */
export const stopTracking = async (sessionId: number): Promise<TrackingSessionResponse> => {
  const res = await api.post<TrackingSessionResponse>(`/api/tracking/${sessionId}/stop`);
  return res.data;
};

/** Get live data for a session (public, no auth) */
export const getLiveData = async (token: string): Promise<TrackingLiveResponse> => {
  const res = await api.get<TrackingLiveResponse>(`/api/tracking/${token}/live`);
  return res.data;
};

/** Get position history (public, no auth) */
export const getHistory = async (token: string): Promise<TrackingHistoryResponse> => {
  const res = await api.get<TrackingHistoryResponse>(`/api/tracking/${token}/history`);
  return res.data;
};

/** Get my tracking sessions (auth required) */
export const getMySessions = async (): Promise<TrackingSessionResponse[]> => {
  const res = await api.get<TrackingSessionResponse[]>('/api/tracking/my-sessions');
  return res.data;
};

/** Get planned route for a tracking session (public, no auth) */
export const getPlannedRoute = async (token: string): Promise<any> => {
  try {
    const res = await api.get(`/api/tracking/${token}/route`);
    return res.data;
  } catch {
    return null;
  }
};

/** Get navigation route from bus position to current target (public, no auth) */
export const getNavRoute = async (token: string): Promise<any> => {
  try {
    const res = await api.get(`/api/tracking/${token}/nav-route`);
    return res.data;
  } catch {
    return null;
  }
};

/** Calculate test route from current position to a manual destination (public, no auth) */
export const getTestRoute = async (token: string, destination: string, latitude?: number, longitude?: number): Promise<any> => {
  try {
    const res = await api.post(`/api/tracking/${token}/test-route`, { destination, latitude, longitude });
    return res.data;
  } catch (err: any) {
    throw new Error(err?.response?.data?.error || 'Route nicht gefunden');
  }
};

/** Get flight position for a tracking session (public, no auth) */
export const getFlightPosition = async (token: string): Promise<FlightResponse> => {
  try {
    const res = await api.get<FlightResponse>(`/api/tracking/${token}/flight`);
    return res.data;
  } catch {
    return { flight: null, reason: 'error' };
  }
};

/** Get active tracking session for a planning (any user, auth required) */
export const getActiveSessionForPlanning = async (planningId: number): Promise<TrackingSessionResponse | null> => {
  try {
    const res = await api.get(`/api/tracking/active-for-planning/${planningId}`);
    const data = res.data;
    // Backend returns { active: false } when no session exists
    if (data?.active === false) return null;
    return data as TrackingSessionResponse;
  } catch {
    return null;
  }
};

/** Get all active tracking sessions (admin only) */
export const getAdminActiveSessions = async (): Promise<TrackingSessionResponse[]> => {
  const res = await api.get<TrackingSessionResponse[]>('/api/tracking/admin/active-sessions');
  return res.data;
};

/** Force-stop any tracking session (admin only) */
export const adminForceStopSession = async (sessionId: number): Promise<TrackingSessionResponse> => {
  const res = await api.post<TrackingSessionResponse>(`/api/tracking/admin/force-stop/${sessionId}`);
  return res.data;
};

/** Build SignalR hub URL */
export const getTrackingHubUrl = (): string => `${API_BASE_URL}/hubs/tracking`;

